import React, { useState, useRef, useEffect, useMemo, ReactNode } from 'react'
import classNames from 'classnames'
import { SelectBase } from 'components/forms/Selects/SelectBase'
import { Autocomplete } from 'transfix-ui/components/Autocomplete'
import { IconButton } from 'transfix-ui/components/IconButton'
import { Spinner } from 'transfix-ui/components/Spinner'
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FilterOptionsState,
  AutocompleteRenderOptionState,
  AutocompleteRenderGroupParams,
} from 'transfix-ui/core'
import { DownArrowIcon, CloseIcon } from 'transfix-icons'
import { ISuggestResult } from './types'

export const isAutosuggestResult = (autocompleteObj: any): autocompleteObj is ISuggestResult => {
  return !!autocompleteObj?.id && !!autocompleteObj?.name
}

export type IFilterAutocompleteProp = string | ISuggestResult | (string | ISuggestResult)[] | null

export interface IFilterAutocompleteProps<T> {
  id: string
  icon?: JSX.Element
  /**
   * Turns on or off default icon spacing of ml4 pl2
   */
  iconSpacing?: boolean
  label: string
  dataTest: string
  /**
   * Array of selectable options.
   */
  options: T[]
  /**
   * Placeholder text for the Autocomplete.
   */
  placeholder: string
  /**
   * Used to determine what gets displayed onto the filter pill.
   */
  getOptionLabel: (optionLabel: T) => string
  /**
   * Comparator function used to determine the selected option from the list of options.
   */
  getOptionSelected: (optionA: T, optionB: T) => boolean
  /**
   * Loading state on the pill, used once you make a selection and an API call is made to load results.
   */
  filterLoading?: boolean
  /**
   * Loading state on the Autocomplete result box, used if you are making an API call to load autocomplete results from the input box.
   */
  resultsLoading?: boolean
  /**
   * updated value from parent component.
   */
  value: T | undefined | null
  /**
   * Function passed in to return the updated value back to the parent component.
   */
  setValue: (a: string | T | NonNullable<T> | (string | T)[] | null) => void
  /**
   * Upon opening the Filter Autocomplete pill, pre-open a list of suggestions.
   */
  quickSuggest?: boolean
  large?: boolean
  /**
   * Add React Node to beginning of input area
   */
  startAdornment?: (option: T | null) => ReactNode
  /**
   * Render the group.
   *
   * @param {AutocompleteRenderGroupParams} param The group to render.
   * @returns {ReactNode}
   */
  renderGroup?: (params: AutocompleteRenderGroupParams) => ReactNode
  /**
   * Render the option, use `getOptionLabel` by default.
   *
   * @param {T} option The option to render.
   * @param {object} state The state of the component.
   * @returns {ReactNode}
   */
  renderOption?: (option: T, state: AutocompleteRenderOptionState, markup?: ReactNode) => ReactNode
  /**
   * A filter function that determines the options that are eligible.
   *
   * @param {T[]} options The options to render.
   * @param {object} state The state of the component.
   * @returns {T[]}
   */
  filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[]
  groupBy?: (option: T) => string
  /**
   * Callback fired when the input value changes.
   */
  onInputChange?: (
    event: React.ChangeEvent<{}>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void
  error?: string
  isAvatar?: boolean
  /**
   * If `true`, the selected option becomes the value of the input
   * when the Autocomplete loses focus unless the user chooses
   * a different option or changes the character string in the input.
   */
  autoSelect?: boolean
  className?: string
  /**
   * Toggle clear flag is used to detect whether we need to clear the autocomplete, used with the filter. Toggle this value from the parent component to clear the inputs.
   */
  toggleClear?: boolean
  renderEmptyState?: () => ReactNode
}

export function FilterAutocomplete<T>({
  autoSelect,
  className,
  dataTest,
  error,
  filterLoading,
  filterOptions,
  getOptionLabel,
  getOptionSelected,
  groupBy,
  icon,
  iconSpacing = true,
  id,
  isAvatar,
  label,
  large,
  onInputChange,
  options,
  placeholder,
  quickSuggest,
  renderEmptyState,
  renderGroup,
  renderOption,
  resultsLoading,
  setValue,
  startAdornment,
  toggleClear,
  value,
}: IFilterAutocompleteProps<T>): JSX.Element {
  const [expandedState, setExpandedState] = useState<boolean>(false)
  const [toggleKey, setToggleKey] = useState<boolean>(false)
  const autocompleteRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (expandedState && autocompleteRef.current) {
      autocompleteRef.current.getElementsByTagName('input')[0].focus()
    }
  }, [expandedState])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clearAutocomplete = () => {
    setToggleKey(!toggleKey)
    setValue(null)
  }

  useEffect(() => {
    if (toggleClear) clearAutocomplete()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleClear])

  const clearFromPillHandler = (e: React.MouseEvent) => {
    clearAutocomplete()
    if (!expandedState) {
      e.stopPropagation()
    }
  }

  const interactionIcon = value ? (
    <IconButton
      onClick={clearFromPillHandler}
      Icon={CloseIcon}
      color='tertiary'
      noPadding
      label='clear filter'
      className='pa0 icon-small clear-filter'
      data-test='clear-button'
      size='large'
    />
  ) : (
    <DownArrowIcon size='medium' />
  )

  const onChangeHandler = (
    event: React.ChangeEvent<{}>,
    value: string | T | NonNullable<T> | (string | T)[] | null,
    reason: AutocompleteChangeReason
  ): void => {
    if (reason === 'selectOption') {
      setValue(value)
    } else if (!value && reason === 'blur') {
      setToggleKey(!toggleKey)
    } else if (reason === 'clear') {
      setValue(null)
    }
  }

  const adornment = useMemo(() => startAdornment && startAdornment(value as T), [
    value,
    startAdornment,
  ])

  const selectedValue = value ? getOptionLabel(value) : ''
  const iconClass = classNames('prefix-icon', {
    pr2: adornment || icon,
  })
  const textClasses = classNames('pr2 text-default nowrap', {
    'ml4 pl2': icon && iconSpacing,
  })

  return (
    <SelectBase
      applied={Boolean(value)}
      hideOnSelect
      autocomplete
      id={label}
      dataTest={dataTest}
      className={className}
      selectButtonType='filter-pill'
      stateCallback={setExpandedState}
      isAvatar={isAvatar}
      displayValue={
        <div className='flex flex-row items-center'>
          <div className={iconClass}>{adornment || icon}</div>
          <b className={textClasses}>{selectedValue || label}</b>
          {filterLoading ? <Spinner position='relative' /> : interactionIcon}
        </div>
      }>
      <Autocomplete
        ref={autocompleteRef}
        dataTest={`${dataTest}-input`}
        id={id}
        options={options}
        getOptionLabel={getOptionLabel}
        placeholder={placeholder}
        clearable
        onChange={onChangeHandler}
        isOptionEqualToValue={getOptionSelected}
        filterOptions={filterOptions}
        filterAutocomplete
        open={quickSuggest}
        toggleClear={toggleKey}
        large={large}
        startAdornment={startAdornment}
        renderGroup={renderGroup}
        renderOption={renderOption}
        groupBy={groupBy}
        onInputChange={onInputChange}
        error={error}
        isLoading={resultsLoading}
        autoSelect={autoSelect}
        autoHighlight={false}
        renderEmptyState={renderEmptyState}
      />
    </SelectBase>
  )
}

export default FilterAutocomplete
